import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./SkillEditor.css";
import { api } from "./api";
import { describeError } from "./errors";
import CustomSelect from "./CustomSelect";
import RoleShareToggle from "./RoleShareToggle";
import UserSearchInput from "./UserSearchInput";

const BLANK_SKILL = {
  name: "", description: "", content: "", is_active: true,
  is_owner: true, can_edit: true, scope: "personal", roles: [], shared_with_users: [],
};

const SCOPE_OPTIONS = [
  { value: "personal", label: "Personal — only you" },
  { value: "role", label: "Role — users with a role" },
  { value: "global", label: "Global — everyone" },
];

const ROOT_FILE = "SKILL.md";
// 1 root SKILL.md + 24 secondary files (mirrors the server's MAX_FILES).
const MAX_FILES = 25;

// Mirror the server's path rules (skills_api._validate_bundle): non-empty,
// not the reserved root name, no leading "/", and no ".." path segment.
// Returns an error string, or null when the path is acceptable.
function validateSecondaryPath(rawPath, files, ignoreIndex = -1) {
  const p = (rawPath || "").trim();
  if (!p) return "File name can't be empty.";
  if (p === ROOT_FILE) return `"${ROOT_FILE}" is the reserved main file.`;
  if (p.startsWith("/")) return "File name can't start with \"/\".";
  if (p.split("/").includes("..")) return "File name can't contain a \"..\" segment.";
  const clash = files.some((f, i) => i !== ignoreIndex && f.path === p);
  if (clash) return `A file named "${p}" already exists.`;
  return null;
}

// Pick a unique default name: reference.md, then reference-2.md, etc.
function nextDefaultPath(files) {
  if (!files.some((f) => f.path === "reference.md")) return "reference.md";
  let n = 2;
  while (files.some((f) => f.path === `reference-${n}.md`)) n += 1;
  return `reference-${n}.md`;
}

export default function SkillEditor({
  open, skillId, isAdmin = false, onClose, onSaved, getSkill, createSkill, patchSkill,
}) {
  const isEdit = !!skillId;
  const [skill, setSkill] = useState(BLANK_SKILL);
  const [userNameMap, setUserNameMap] = useState({});
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  // Secondary files only ({path, content}); the root SKILL.md is `skill.content`.
  const [files, setFiles] = useState([]);
  const [activePath, setActivePath] = useState(ROOT_FILE);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSelectedTemplate("");
    setActivePath(ROOT_FILE);
    if (isEdit) {
      getSkill(skillId).then((s) => {
        setSkill({
          name: s.name, description: s.description || "", content: s.content || "",
          is_active: s.is_active, is_owner: s.is_owner !== false,
          can_edit: s.can_edit !== false, scope: s.scope || "personal",
          roles: s.roles || [], shared_with_users: s.shared_with_users || [],
        });
        // The server never returns SKILL.md in `files`, but guard anyway so it
        // can't sneak into the secondary list.
        setFiles((s.files || []).filter((f) => (f.path || "").trim() !== ROOT_FILE));
      }).catch((e) => setError(e.message));
    } else {
      setSkill(BLANK_SKILL);
      setFiles([]);
    }
  }, [open, skillId, isEdit, getSkill]);

  // Read-only when the caller can't edit this skill (a normal user viewing a
  // role/global skill, or a non-owner). Editing would only 403 at save.
  const readOnly = isEdit && skill.can_edit === false;

  useEffect(() => {
    if (!open) return;
    api.listSkillTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, [open]);

  const addSharedUser = (user) => {
    const email = (user.email || "").trim().toLowerCase();
    if (!email) return;
    if (user.firstName || user.lastName) {
      setUserNameMap((prev) => ({ ...prev, [email]: `${user.firstName || ""} ${user.lastName || ""}`.trim() }));
    }
    setSkill((s) => (s.shared_with_users.includes(email)
      ? s
      : { ...s, shared_with_users: [...s.shared_with_users, email] }));
  };

  const removeSharedUser = (email) =>
    setSkill((s) => ({ ...s, shared_with_users: s.shared_with_users.filter((u) => u !== email) }));

  const applyTemplate = (key) => {
    setSelectedTemplate(key);
    if (!key) return;
    const tpl = templates.find((t) => t.key === key);
    if (!tpl) return;
    if (skill.content && !window.confirm("Replace current content with the template?")) {
      setSelectedTemplate("");
      return;
    }
    setSkill((s) => ({
      ...s, name: s.name || tpl.name,
      description: s.description || tpl.description, content: tpl.content,
    }));
  };

  // --- Secondary file management -------------------------------------------
  const atFileLimit = files.length >= MAX_FILES - 1; // -1 for the root SKILL.md

  const addFile = () => {
    if (readOnly || atFileLimit) return;
    const path = nextDefaultPath(files);
    setFiles((prev) => [...prev, { path, content: "" }]);
    setActivePath(path);
    setError(null);
  };

  const renameFile = (index) => {
    if (readOnly) return;
    const current = files[index];
    if (!current) return;
    const next = window.prompt("New file name (e.g. reference.md)", current.path);
    if (next == null) return; // cancelled
    const trimmed = next.trim();
    if (trimmed === current.path) return;
    const err = validateSecondaryPath(trimmed, files, index);
    if (err) { setError(err); return; }
    const wasActive = activePath === current.path;
    setFiles((prev) => prev.map((f, i) => i === index ? { ...f, path: trimmed } : f));
    if (wasActive) setActivePath(trimmed);
    setError(null);
  };

  const deleteFile = (index) => {
    if (readOnly) return;
    const target = files[index];
    if (!target) return;
    setFiles((prev) => prev.filter((_, i) => i !== index));
    if (activePath === target.path) setActivePath(ROOT_FILE);
    setError(null);
  };

  const updateActiveContent = (value) => {
    if (activePath === ROOT_FILE) {
      setSkill((s) => ({ ...s, content: value }));
    } else {
      setFiles((prev) => prev.map((f) => f.path === activePath ? { ...f, content: value } : f));
    }
  };

  const activeFile = files.find((f) => f.path === activePath);
  const activeContent = activePath === ROOT_FILE ? skill.content : (activeFile?.content || "");
  const activePlaceholder = activePath === ROOT_FILE
    ? "# My Skill\n\n## Section\n- bullet"
    : "# Reference\n\nSupporting markdown for this skill.";

  const save = async () => {
    setError(null);
    if (!skill.name.trim()) { setError("Name is required."); return; }
    if (skill.scope === "role" && skill.roles.length === 0) {
      setError("Pick at least one role for a role-type skill."); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: skill.name.trim(),
        description: skill.description,
        content: skill.content,
        files,
      };
      if (selectedTemplate) payload.template_key = selectedTemplate;
      payload.shared_with_users = skill.shared_with_users;
      if (isEdit) {
        if (isAdmin) payload.scope = skill.scope;
        if (skill.scope === "role") payload.roles = skill.roles;
        await patchSkill(skillId, payload);
      } else {
        payload.scope = isAdmin ? skill.scope : "personal";
        if (payload.scope === "role") payload.roles = skill.roles;
        await createSkill({ ...payload, is_active: true });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(describeError(e) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const showScopePicker = isAdmin && !readOnly;
  const showRolesEditor = isAdmin && !readOnly && skill.scope === "role";

  return (
    <div className="se-backdrop">
      <div className="se-modal">
        <div className="se-header">
          <h2>{readOnly ? "View Skill" : isEdit ? "Edit Skill" : "New Skill"}</h2>
          <button className="se-close" onClick={onClose} title="Close">×</button>
        </div>
        {readOnly && (
          <div className="se-hint" style={{ padding: "0 16px" }}>
            {skill.scope === "global" ? "Global skill — managed by an admin."
              : skill.scope === "role" ? "Role skill — managed by an admin."
              : "Shared with you — only the owner can edit."}{" "}
            You can still turn it on/off for your own chats from the Skills list.
          </div>
        )}
        {error && <div className="se-error">{error}</div>}

        <div className="se-fields">
          {!isEdit && (
            <div className="se-field">
              <label>Start from a template</label>
              <CustomSelect
                value={selectedTemplate}
                options={[
                  { value: "", label: "Pick a template (optional)" },
                  ...templates.map((t) => ({ value: t.key, label: t.name, hint: t.description })),
                ]}
                onChange={(v) => applyTemplate(v)}
                placeholder="Pick a template (optional)"
                ariaLabel="Start from a template"
              />
            </div>
          )}

          {showScopePicker && (
            <div className="se-field">
              <label>Who can use this skill</label>
              <CustomSelect
                value={skill.scope}
                options={SCOPE_OPTIONS}
                onChange={(v) => setSkill((s) => ({ ...s, scope: v }))}
                ariaLabel="Skill scope"
              />
            </div>
          )}

          {showRolesEditor && (
            <div className="se-field">
              <label>Roles that see this skill</label>
              <RoleShareToggle
                value={skill.roles}
                onChange={(roles) => setSkill((s) => ({ ...s, roles }))}
              />
              {skill.roles.length === 0 && <span className="se-hint">No roles yet — pick at least one.</span>}
            </div>
          )}

          {!readOnly && (
            <div className="se-field">
              <label>Shared with specific people</label>
              {skill.shared_with_users.length > 0 && (
                <div className="se-user-chips">
                  {skill.shared_with_users.map((email) => (
                    <span key={email} className="se-user-chip">
                      {userNameMap[email] || email}
                      <button type="button" onClick={() => removeSharedUser(email)} aria-label={`Remove ${email}`}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <UserSearchInput onSelectUser={addSharedUser} />
            </div>
          )}

          <div className="se-row">
            <div className="se-field se-field-grow">
              <label>Name</label>
              <input
                value={skill.name}
                onChange={(e) => setSkill((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g., Customer Support, Code Review"
                maxLength={120} autoFocus={!isEdit} readOnly={readOnly}
              />
            </div>
          </div>

          <div className="se-field">
            <label>Description (optional)</label>
            <input
              value={skill.description}
              onChange={(e) => setSkill((s) => ({ ...s, description: e.target.value }))}
              placeholder="One line about when to use this skill"
              maxLength={400} readOnly={readOnly}
            />
          </div>

          <div className="se-field se-field-grow se-field-content">
            <div className="se-content-header">
              <label>Markdown files</label>
              <button className="se-preview-toggle" onClick={() => setShowPreview((v) => !v)} type="button">
                {showPreview ? "Hide preview" : "Show preview"}
              </button>
            </div>
            <div className="se-files-layout">
              <SkillFileList
                files={files}
                activePath={activePath}
                readOnly={readOnly}
                atFileLimit={atFileLimit}
                onSelect={setActivePath}
                onAdd={addFile}
                onRename={renameFile}
                onDelete={deleteFile}
              />
              <div className={`se-content-grid ${showPreview ? "with-preview" : ""}`}>
                <textarea
                  className="se-textarea"
                  value={activeContent}
                  onChange={(e) => updateActiveContent(e.target.value)}
                  placeholder={activePlaceholder}
                  readOnly={readOnly}
                />
                {showPreview && (
                  <div className="se-preview">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{activeContent || "_Preview will appear here_"}</ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
            <div className="se-hint">
              The agent reads these as plain markdown. SKILL.md is the main file; add secondary
              files for longer reference material. Skills load once at the start of each new chat.
            </div>
          </div>
        </div>

        <div className="se-actions">
          <button className="ca-btn" onClick={onClose} disabled={saving}>
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button className="se-save ca-btn ca-btn--primary" onClick={save} disabled={saving || !skill.name.trim()}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create skill"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SkillFileList({
  files, activePath, readOnly, atFileLimit, onSelect, onAdd, onRename, onDelete,
}) {
  const rootActive = activePath === ROOT_FILE;
  return (
    <div className="se-file-list">
      <ul className="se-file-items">
        <li className={`se-file-item se-file-item--root ${rootActive ? "is-active" : ""}`}>
          <button
            type="button"
            className="se-file-name"
            onClick={() => onSelect(ROOT_FILE)}
            title="Main skill file"
          >
            <span className="se-file-label">{ROOT_FILE}</span>
            <span className="se-file-tag">main</span>
          </button>
        </li>
        {files.map((f, i) => (
          <li key={f.path} className={`se-file-item ${activePath === f.path ? "is-active" : ""}`}>
            <button
              type="button"
              className="se-file-name"
              onClick={() => onSelect(f.path)}
              title={f.path}
            >
              <span className="se-file-label">{f.path}</span>
            </button>
            {!readOnly && (
              <span className="se-file-tools">
                <button
                  type="button"
                  className="se-file-tool"
                  onClick={() => onRename(i)}
                  title="Rename file"
                  aria-label={`Rename ${f.path}`}
                >✎</button>
                <button
                  type="button"
                  className="se-file-tool se-file-tool--delete"
                  onClick={() => onDelete(i)}
                  title="Delete file"
                  aria-label={`Delete ${f.path}`}
                >×</button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {!readOnly && (
        <button
          type="button"
          className="se-file-add"
          onClick={onAdd}
          disabled={atFileLimit}
          title={atFileLimit ? `Limit reached (max ${MAX_FILES} files)` : "Add a secondary file"}
        >
          + Add file
        </button>
      )}
      {!readOnly && atFileLimit && (
        <span className="se-hint">Max {MAX_FILES} files (1 main + {MAX_FILES - 1} secondary).</span>
      )}
    </div>
  );
}
